import { action, makeObservable, observable, runInAction } from 'mobx';
import { uploadFiles } from 'global/utils/uploadFiles';
import axios from 'axios';
import { UploadUrls } from 'types/carrierFileService';
import {
  doesEnrollmentHaveCensus,
  getCensusDownloadUrls,
  getPrepopulatedCensusDownloadUrl,
} from 'api/operations/censusService';
import { celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { downloadCensusTemplate } from 'global/utils/downloadFiles';
import { getEnrollmentPeriod } from 'api/operations/enrollmentService';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { CensusModuleDependencies, ICensusModule } from '../iCensusModule';

export interface CensusEnrollmentDomainDependencies extends CensusModuleDependencies {
  enrollmentPeriodId: string;
}

export class CensusEnrollmentModule extends ICensusModule<CensusEnrollmentDomainDependencies> {
  private _rateSegment: RateSegment | null = null;

  public async getRateSegmentData(): Promise<void> {
    const { rateSegment } = await getEnrollmentPeriod(this._domainDependencies.enrollmentPeriodId);
    runInAction(() => {
      this._rateSegment = rateSegment;
    });
  }

  public async uploadExistingCensus(): Promise<void> {
    const info = await doesEnrollmentHaveCensus(
      this._domainDependencies.groupId,
      this._domainDependencies.enrollmentPeriodId,
    );
    runInAction(() => {
      this._existingCensusInfo = info;
    });
  }

  public async uploadCensus(): Promise<void> {
    if (!this._rateSegment) {
      throw new Error('Rate segment has not been set! Check the page initialization of the module.');
    }
    if (!this._uploadedFiles) {
      throw new Error('No files uploaded!');
    }

    this.setIsLoading(true);
    const uploadedFileNames = Object.keys(this._uploadedFiles).join(':');

    // send file names and get list of urls
    const res = await axios<UploadUrls>({
      url: `https://${process.env.REACT_APP_API_BASE_URL}/censusV2/Prod/upload?groupId=${this._domainDependencies.groupId}&periodId=${this._domainDependencies.enrollmentPeriodId}&fileNames=${uploadedFileNames}&rateSegment=${this._rateSegment}`,
      withCredentials: true,
    });

    try {
      await runInAction(async () => {
        await uploadFiles(this._uploadedFiles!, res.data);
      });
    } catch (err) {
      this.setCensusHasErrors(true);
    } finally {
      this.setIsLoading(false);
    }
  }

  public async getPreviouslyUploadedFiles(): Promise<void> {
    if (celticsRights(this._rootStore.userStore.userRoles)) {
      const urls = await getCensusDownloadUrls(
        this._domainDependencies.groupId,
        this._domainDependencies.enrollmentPeriodId,
      );
      if (urls.length > 0) {
        this._setPreviouslyUploadedFilesUrls(urls);
      }
    }
  }

  public async downloadCensusTemplate(): Promise<void> {
    if (this.existingCensusInfo?.hasPrePopulatedCensus) {
      const url = await getPrepopulatedCensusDownloadUrl(
        this._domainDependencies.groupId,
        this._domainDependencies.enrollmentPeriodId,
      );
      downloadByUrl(url);
    } else {
      await downloadCensusTemplate(
        `https://${process.env.REACT_APP_API_BASE_URL}/censusV2Binary/Prod/template?rateSegment=${this._rateSegment}`,
      );
    }
  }

  constructor(domainDependencies: CensusEnrollmentDomainDependencies) {
    super(domainDependencies);
    makeObservable<CensusEnrollmentModule, '_rateSegment'>(this, {
      _rateSegment: observable,

      getRateSegmentData: action.bound,
      uploadExistingCensus: action.bound,
      uploadCensus: action.bound,
      getPreviouslyUploadedFiles: action.bound,
      downloadCensusTemplate: action.bound,
    });
  }
}
